import { isActionComplete } from '@agentic-flow-studio/shared/lib/clinic-rules';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@agentic-flow-studio/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import { Switch } from '@agentic-flow-studio/shared/ui/switch';
import { Textarea } from '@agentic-flow-studio/shared/ui/textarea';
import {
  AlertCircle,
  ArrowLeft,
  History,
  Loader2,
  MessageSquareText,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useAutomationRules,
  useCannedResponses,
  useClinicCategories,
  useCreateCannedResponse,
  useCreateRule,
  useDeleteCannedResponse,
  useDeleteRule,
  useRuleExecutions,
  useToggleRule,
  useUpdateCannedResponse,
  useUpdateRule,
  useWorkspaceMembers,
} from '@/hooks/useClinic';
import type {
  AutomationRule,
  CannedResponse,
  RuleActionDef,
  RuleActionType,
  RuleConditionDef,
  RuleConditionOperator,
  RuleTriggerType,
} from '@/types/clinic';
import { trpc } from '@/utils/trpc';

const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'domainCategory', label: 'Domain Category' },
  { value: 'actionType', label: 'Action Type' },
  { value: 'threadState', label: 'Thread State' },
  { value: 'senderEmail', label: 'Sender Email' },
  { value: 'senderDomain', label: 'Sender Domain' },
  { value: 'labels', label: 'Gmail Labels' },
  { value: 'coreAsk', label: 'Core Ask (text)' },
  { value: 'hoursSinceLastActivity', label: 'Hours Since Last Activity (scheduled only)' },
];

const OPERATOR_OPTIONS: { value: RuleConditionOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'is one of (comma-separated)' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'matchesDomain', label: 'matches domain (or subdomain)' },
];

const ACTION_TYPE_OPTIONS: { value: RuleActionType; label: string }[] = [
  { value: 'add_label', label: 'Add Gmail label' },
  { value: 'remove_label', label: 'Remove Gmail label' },
  { value: 'create_task', label: 'Create task' },
  { value: 'link_contact', label: 'Link to Contact' },
  { value: 'set_category', label: 'Set category' },
  { value: 'set_priority', label: 'Set priority weight' },
  { value: 'auto_reply', label: 'Auto-reply with canned response' },
];

interface ConditionRow {
  field: string;
  operator: RuleConditionOperator;
  value: string;
}

interface ActionRow {
  type: RuleActionType;
  params: Record<string, string>;
}

function conditionDefToRow(c: RuleConditionDef): ConditionRow {
  return {
    field: c.field,
    operator: c.operator,
    value: Array.isArray(c.value) ? c.value.join(', ') : String(c.value),
  };
}

function conditionRowToDef(row: ConditionRow): RuleConditionDef {
  if (row.operator === 'in') {
    return {
      field: row.field,
      operator: row.operator,
      value: row.value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    };
  }
  if (row.operator === 'gt' || row.operator === 'lt') {
    return { field: row.field, operator: row.operator, value: Number(row.value) || 0 };
  }
  return { field: row.field, operator: row.operator, value: row.value };
}

function actionDefToRow(a: RuleActionDef): ActionRow {
  const params: Record<string, string> = {};
  Object.entries(a.params || {}).forEach(([k, v]) => {
    params[k] = v === undefined || v === null ? '' : String(v);
  });
  return { type: a.type, params };
}

const NUMERIC_ACTION_PARAMS = new Set([
  'assignedTo',
  'priority',
  'dueInHours',
  'contactId',
  'categoryId',
  'weight',
  'cannedResponseId',
  'tagId',
]);

function actionRowToDef(row: ActionRow): RuleActionDef {
  const params: Record<string, unknown> = {};
  Object.entries(row.params).forEach(([k, v]) => {
    if (v === '') return;
    params[k] = NUMERIC_ACTION_PARAMS.has(k) ? Number(v) : v;
  });
  return { type: row.type, params };
}

export default function AutomationRules() {
  const navigate = useNavigate();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const [activeTab, setActiveTab] = useState<'rules' | 'canned'>('rules');
  const [editingRule, setEditingRule] = useState<AutomationRule | 'new' | null>(null);
  const [historyRuleId, setHistoryRuleId] = useState<number | null>(null);
  const [editingCanned, setEditingCanned] = useState<CannedResponse | 'new' | null>(null);

  const rulesQuery = useAutomationRules();
  const cannedQuery = useCannedResponses();
  const toggleRule = useToggleRule();
  const deleteRule = useDeleteRule();
  const deleteCanned = useDeleteCannedResponse();

  const rules = rulesQuery.data || [];
  const cannedResponses = cannedQuery.data || [];

  const handleToggle = async (rule: AutomationRule) => {
    if (isReadOnly) return;
    try {
      await toggleRule.mutateAsync({ id: rule.id, isEnabled: !rule.isEnabled });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update rule';
      toast.error(message);
    }
  };

  const handleDeleteRule = async (rule: AutomationRule) => {
    if (isReadOnly) return;
    if (!confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      await deleteRule.mutateAsync(rule.id);
      toast.success('Rule deleted');
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const handleDeleteCanned = async (canned: CannedResponse) => {
    if (isReadOnly) return;
    if (!confirm(`Delete canned response "${canned.name}"?`)) return;
    try {
      await deleteCanned.mutateAsync(canned.id);
      toast.success('Canned response deleted');
    } catch {
      toast.error('Failed to delete canned response');
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/settings/clinic`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="display font-display text-3xl font-light tracking-tight">
              Automation Rules
            </h1>
            <p className="text-muted-foreground mt-1">
              If-this-then-that routing for incoming threads, plus shared canned responses.
            </p>
          </div>
        </div>
      </div>

      {isReadOnly && (
        <div className="bg-muted-foreground/10 border border-border text-foreground p-4 rounded-xl flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">Read-only View</p>
            <p className="opacity-90">You have read-only access to this workspace.</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 p-1 bg-muted/50 rounded-xl w-fit">
        <Button
          variant={activeTab === 'rules' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('rules')}
        >
          <Zap className="h-4 w-4 mr-2" /> Rules
        </Button>
        <Button
          variant={activeTab === 'canned' ? 'secondary' : 'ghost'}
          size="sm"
          className="rounded-lg"
          onClick={() => setActiveTab('canned')}
        >
          <MessageSquareText className="h-4 w-4 mr-2" /> Canned Responses
        </Button>
      </div>

      {activeTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setEditingRule('new')} disabled={isReadOnly} className="gap-2">
              <Plus className="h-4 w-4" /> New Rule
            </Button>
          </div>

          {rulesQuery.isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rules.length === 0 ? (
            <div className="p-12 border border-dashed rounded-xl text-center">
              <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <p className="text-muted-foreground">No automation rules yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules
                .slice()
                .sort((a, b) => a.priority - b.priority)
                .map((rule) => (
                  <Card key={rule.id} className="border-border/50 bg-card/50 backdrop-blur-sm">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{rule.name}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {rule.triggerType === 'on_ingest' ? 'On new email' : 'Scheduled'}
                            </Badge>
                            {rule.stopOnMatch && (
                              <Badge variant="outline" className="text-[10px]">
                                Stops further rules
                              </Badge>
                            )}
                          </div>
                          {rule.description && (
                            <p className="text-xs text-muted-foreground">{rule.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Switch
                            checked={rule.isEnabled}
                            onCheckedChange={() => handleToggle(rule)}
                            disabled={isReadOnly}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setHistoryRuleId(rule.id)}
                            title="Execution history"
                          >
                            <History className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditingRule(rule)}
                            disabled={isReadOnly}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteRule(rule)}
                            disabled={isReadOnly}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="text-muted-foreground font-medium">If</span>
                        {rule.conditions.map((c, i) => (
                          <Badge key={i} variant="secondary" className="font-normal">
                            {c.field} {c.operator}{' '}
                            {Array.isArray(c.value) ? c.value.join('/') : String(c.value)}
                          </Badge>
                        ))}
                        <span className="text-muted-foreground font-medium ml-1">then</span>
                        {rule.actions.map((a, i) => (
                          <Badge key={i} className="font-normal">
                            {ACTION_TYPE_OPTIONS.find((o) => o.value === a.type)?.label || a.type}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'canned' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setEditingCanned('new')} disabled={isReadOnly} className="gap-2">
              <Plus className="h-4 w-4" /> New Canned Response
            </Button>
          </div>

          {cannedQuery.isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : cannedResponses.length === 0 ? (
            <div className="p-12 border border-dashed rounded-xl text-center">
              <MessageSquareText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <p className="text-muted-foreground">No canned responses yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cannedResponses.map((c) => (
                <Card key={c.id} className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader className="flex flex-row items-start justify-between pb-2">
                    <CardTitle className="text-sm">{c.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setEditingCanned(c)}
                        disabled={isReadOnly}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteCanned(c)}
                        disabled={isReadOnly}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {c.subjectTemplate && (
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Subject: {c.subjectTemplate}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                      {c.bodyTemplate}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {editingRule !== null && (
        <RuleFormDialog
          rule={editingRule === 'new' ? null : editingRule}
          onClose={() => setEditingRule(null)}
        />
      )}

      {editingCanned !== null && (
        <CannedResponseFormDialog
          canned={editingCanned === 'new' ? null : editingCanned}
          onClose={() => setEditingCanned(null)}
        />
      )}

      {historyRuleId !== null && (
        <RuleHistoryDialog ruleId={historyRuleId} onClose={() => setHistoryRuleId(null)} />
      )}
    </div>
  );
}

function RuleFormDialog({ rule, onClose }: { rule: AutomationRule | null; onClose: () => void }) {
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const mailTagsQuery = trpc.mailTags.list.useQuery();
  const categoriesQuery = useClinicCategories();
  const membersQuery = useWorkspaceMembers();
  const cannedQuery = useCannedResponses();

  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [triggerType, setTriggerType] = useState<RuleTriggerType>(rule?.triggerType || 'on_ingest');
  const [priority, setPriority] = useState(rule?.priority ?? 0);
  const [stopOnMatch, setStopOnMatch] = useState(rule?.stopOnMatch ?? false);
  const [conditions, setConditions] = useState<ConditionRow[]>(
    rule?.conditions.map(conditionDefToRow) ?? [
      { field: 'domainCategory', operator: 'equals', value: '' },
    ]
  );
  const [actions, setActions] = useState<ActionRow[]>(
    rule?.actions.map(actionDefToRow) ?? [{ type: 'add_label', params: {} }]
  );
  const [isSaving, setIsSaving] = useState(false);

  const mailTags = mailTagsQuery.data?.items || [];
  const categories = categoriesQuery.data || [];
  const members = membersQuery.data || [];
  const cannedResponses = cannedQuery.data || [];

  const updateCondition = (index: number, patch: Partial<ConditionRow>) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };
  const updateAction = (index: number, patch: Partial<ActionRow>) => {
    setActions((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };
  const updateActionParam = (index: number, key: string, value: string) => {
    setActions((prev) =>
      prev.map((a, i) => (i === index ? { ...a, params: { ...a.params, [key]: value } } : a))
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (conditions.length === 0) {
      toast.error('At least one condition is required');
      return;
    }
    if (actions.length === 0) {
      toast.error('At least one action is required');
      return;
    }
    // `actionRowToDef` strips empty-string params, so a half-filled row serialises
    // to `params: {}`. The backend validates against the same shared schema and
    // would reject it with a 400 — catch it here where we can name the action.
    const incomplete = actions.findIndex((a) => !isActionComplete(actionRowToDef(a)));
    if (incomplete !== -1) {
      toast.error(
        `Action ${incomplete + 1} (${actions[incomplete].type}) is missing a required value`
      );
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        triggerType,
        conditions: conditions.map(conditionRowToDef),
        actions: actions.map(actionRowToDef),
        priority,
        stopOnMatch,
      };

      if (rule) {
        await updateRule.mutateAsync({ id: rule.id, ...payload });
        toast.success('Rule updated');
      } else {
        await createRule.mutateAsync(payload);
        toast.success('Rule created');
      }
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save rule';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit Rule' : 'New Automation Rule'}</DialogTitle>
          <DialogDescription>
            Define what triggers this rule and what it should do automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Escalate VIP client emails"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Trigger</label>
              <Select
                value={triggerType}
                onValueChange={(v) => setTriggerType(v as RuleTriggerType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_ingest">On new email</SelectItem>
                  <SelectItem value="scheduled">Scheduled (time-based)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Priority (lower runs first)
              </label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/50 p-3">
            <div>
              <p className="text-xs font-medium">Stop processing further rules on match</p>
              <p className="text-[11px] text-muted-foreground">
                If this rule matches, lower-priority rules won&apos;t be evaluated for the same
                thread.
              </p>
            </div>
            <Switch checked={stopOnMatch} onCheckedChange={setStopOnMatch} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">
                Conditions (all must match)
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setConditions((prev) => [
                    ...prev,
                    { field: 'domainCategory', operator: 'equals', value: '' },
                  ])
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {conditions.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Select value={c.field} onValueChange={(v) => updateCondition(i, { field: v })}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_OPTIONS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={c.operator}
                  onValueChange={(v) =>
                    updateCondition(i, { operator: v as RuleConditionOperator })
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATOR_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={c.value}
                  onChange={(e) => updateCondition(i, { value: e.target.value })}
                  placeholder="value"
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => setConditions((prev) => prev.filter((_, idx) => idx !== i))}
                  disabled={conditions.length === 1}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">Actions</label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActions((prev) => [...prev, { type: 'add_label', params: {} }])}
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {actions.map((a, i) => (
              <div
                key={i}
                className="flex gap-2 items-start p-3 rounded-lg border border-border/50"
              >
                <Select
                  value={a.type}
                  onValueChange={(v) => updateAction(i, { type: v as RuleActionType, params: {} })}
                >
                  <SelectTrigger className="w-[240px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex-1 flex flex-wrap gap-2">
                  {(a.type === 'add_label' || a.type === 'remove_label') && (
                    <Select
                      value={a.params.tagId || ''}
                      onValueChange={(v) => updateActionParam(i, 'tagId', v)}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Choose tag" />
                      </SelectTrigger>
                      <SelectContent>
                        {mailTags.map((t) => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {a.type === 'create_task' && (
                    <>
                      <Input
                        className="w-[200px]"
                        placeholder="Task title (optional)"
                        value={a.params.title || ''}
                        onChange={(e) => updateActionParam(i, 'title', e.target.value)}
                      />
                      <Select
                        value={a.params.assignedTo || ''}
                        onValueChange={(v) => updateActionParam(i, 'assignedTo', v)}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Assign to (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {members.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {m.fullName || m.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        className="w-[120px]"
                        placeholder="Due in hours"
                        value={a.params.dueInHours || ''}
                        onChange={(e) => updateActionParam(i, 'dueInHours', e.target.value)}
                      />
                    </>
                  )}

                  {a.type === 'link_contact' && (
                    <Input
                      type="number"
                      className="w-[160px]"
                      placeholder="Contact ID"
                      value={a.params.contactId || ''}
                      onChange={(e) => updateActionParam(i, 'contactId', e.target.value)}
                    />
                  )}

                  {a.type === 'set_category' && (
                    <Select
                      value={a.params.categoryId || ''}
                      onValueChange={(v) => updateActionParam(i, 'categoryId', v)}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Choose category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {a.type === 'set_priority' && (
                    <Input
                      type="number"
                      className="w-[140px]"
                      placeholder="Weight (-10 to 10)"
                      value={a.params.weight || ''}
                      onChange={(e) => updateActionParam(i, 'weight', e.target.value)}
                    />
                  )}

                  {a.type === 'auto_reply' && (
                    <Select
                      value={a.params.cannedResponseId || ''}
                      onValueChange={(v) => updateActionParam(i, 'cannedResponseId', v)}
                    >
                      <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Choose canned response" />
                      </SelectTrigger>
                      <SelectContent>
                        {cannedResponses.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => setActions((prev) => prev.filter((_, idx) => idx !== i))}
                  disabled={actions.length === 1}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {rule ? 'Save Changes' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CannedResponseFormDialog({
  canned,
  onClose,
}: {
  canned: CannedResponse | null;
  onClose: () => void;
}) {
  const createCanned = useCreateCannedResponse();
  const updateCanned = useUpdateCannedResponse();
  const [name, setName] = useState(canned?.name || '');
  const [subjectTemplate, setSubjectTemplate] = useState(canned?.subjectTemplate || '');
  const [bodyTemplate, setBodyTemplate] = useState(canned?.bodyTemplate || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !bodyTemplate.trim()) {
      toast.error('Name and body are required');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        subjectTemplate: subjectTemplate.trim() || undefined,
        bodyTemplate,
      };
      if (canned) {
        await updateCanned.mutateAsync({ id: canned.id, ...payload });
        toast.success('Canned response updated');
      } else {
        await createCanned.mutateAsync(payload);
        toast.success('Canned response created');
      }
      onClose();
    } catch {
      toast.error('Failed to save canned response');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{canned ? 'Edit Canned Response' : 'New Canned Response'}</DialogTitle>
          <DialogDescription>
            Use <code>{'{{senderName}}'}</code>, <code>{'{{senderEmail}}'}</code>,{' '}
            <code>{'{{coreAsk}}'}</code>, or <code>{'{{subject}}'}</code> as placeholders.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Subject (optional)</label>
            <Input value={subjectTemplate} onChange={(e) => setSubjectTemplate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Body</label>
            <Textarea
              rows={6}
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {canned ? 'Save Changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleHistoryDialog({ ruleId, onClose }: { ruleId: number; onClose: () => void }) {
  const executionsQuery = useRuleExecutions(ruleId);
  const executions = useMemo(() => executionsQuery.data || [], [executionsQuery.data]);

  const statusColor: Record<string, string> = {
    success: 'bg-muted-foreground/10 text-foreground',
    partial: 'bg-muted-foreground/10 text-foreground',
    failed: 'bg-muted-foreground/10 text-foreground',
    skipped: 'bg-muted text-muted-foreground',
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Execution History</DialogTitle>
          <DialogDescription>Most recent runs of this rule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {executionsQuery.isLoading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : executions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No executions yet.</p>
          ) : (
            executions.map((e) => (
              <div key={e.id} className="p-3 rounded-lg border border-border/50 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">{e.threadId}</span>
                  <Badge className={statusColor[e.status] || ''} variant="outline">
                    {e.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {e.actionsRun.map((a, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] font-normal">
                      {a.type}: {a.status}
                    </Badge>
                  ))}
                </div>
                {e.errorMessage && <p className="text-[11px] text-destructive">{e.errorMessage}</p>}
                <p className="text-[10px] text-muted-foreground">
                  {new Date(e.createdAt * 1000).toLocaleString()}
                </p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
